import React, { ReactNode, useEffect, useState } from 'react';
import useElementOnScreen from 'hooks/use-element-on-screen';
import NonKeyedListMapper from 'components/core/NonKeyedListMapper';
import {
  Observer,
  ScrollContainer,
  StyleProps,
} from './styles';

interface InfiniteScrollProps<T> extends Partial<StyleProps> {
  children: ReactNode | ((props: T) => ReactNode);
  className?: string;
  disableClientPagination?: boolean;
  list?: T[];
  onLoadMore?: () => void;
  pageSize?: number;
}

export default function InfiniteScroll<T>({
  admin = false,
  children,
  className,
  disableClientPagination,
  disableScroll = false,
  list,
  offset = 1,
  onLoadMore,
  pageSize = 10,
}: InfiniteScrollProps<T>) {
  const [renderLimit, setRenderLimit] = useState(pageSize);
  const [limiterElementRef, isVisible] = useElementOnScreen<HTMLDivElement>({ threshold: 0 });

  useEffect(() => {
    if (isVisible) {
      if (list && !disableClientPagination) {
        setRenderLimit((currentLimit) => currentLimit + pageSize);
      }
      if (onLoadMore && renderLimit >= (list?.length || 0)) {
        onLoadMore();
      }
    }
  }, [disableClientPagination, isVisible, pageSize, list?.length]);

  return (
    <ScrollContainer admin={admin} className={className} disableScroll={disableScroll}>
      {typeof children === 'function' && list && (
        <NonKeyedListMapper list={disableClientPagination ? list : list.slice(0, renderLimit)}>
          {(key: string, itemProps: T) => children({ ...itemProps, key })}
        </NonKeyedListMapper>
      )}
      {typeof children !== 'function' && children}
      <Observer offset={offset} ref={limiterElementRef} />
    </ScrollContainer>
  );
}
